"use client"

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import { getJWTToken } from "@/lib/api"
import {
  b2bApi,
  getActiveOrgId,
  setActiveOrgId,
  PENDING_INVITE_KEY,
  type OrgSummary,
} from "@/lib/b2b"

interface OrgContextValue {
  loading: boolean
  orgs: OrgSummary[]
  activeOrg: OrgSummary | null
  switchOrg: (orgId: string) => void
  refreshOrgs: () => Promise<OrgSummary[]>
  error: string | null
}

const OrgContext = createContext<OrgContextValue | null>(null)

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error("useOrg must be used inside <OrgProvider>")
  return ctx
}

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const [loading, setLoading] = useState(true)
  const [orgs, setOrgs] = useState<OrgSummary[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refreshOrgs = useCallback(async () => {
    try {
      const resp = await b2bApi.myOrgs()
      const list = resp.organizations || []
      setOrgs(list)
      setError(null)
      const stored = getActiveOrgId()
      const valid = list.find((o) => o.org_id === stored) || list[0] || null
      setActiveId(valid?.org_id ?? null)
      setActiveOrgId(valid?.org_id ?? null)
      return list
    } catch (e) {
      // Auth failures bubble to the guard; anything else is a soft error the
      // shell can surface without dead-ending the user.
      setError(e instanceof Error ? e.message : "Could not load organizations")
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Not logged in → guard component redirects; skip the fetch to avoid a 401 loop.
    if (!getJWTToken()) {
      setLoading(false)
      return
    }
    // A pending invite token (stored by the invite landing page pre-login)
    // is accepted before the org list is first loaded.
    const pending = typeof window !== "undefined" ? localStorage.getItem(PENDING_INVITE_KEY) : null
    const boot = async () => {
      if (pending) {
        try {
          const res = await b2bApi.acceptInvite(pending)
          setActiveOrgId(res.org_id)
        } catch {
          // invalid/expired token — drop it silently, landing page already messaged
        } finally {
          localStorage.removeItem(PENDING_INVITE_KEY)
        }
      }
      await refreshOrgs()
    }
    void boot()
  }, [refreshOrgs])

  const switchOrg = useCallback(
    (orgId: string) => {
      setActiveId(orgId)
      setActiveOrgId(orgId)
      router.refresh()
    },
    [router],
  )

  const value = useMemo<OrgContextValue>(() => {
    const activeOrg = orgs.find((o) => o.org_id === activeId) ?? null
    return { loading, orgs, activeOrg, switchOrg, refreshOrgs, error }
  }, [loading, orgs, activeId, switchOrg, refreshOrgs, error])

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}
